import { useState } from 'react';
import { X, Sparkles } from 'lucide-react';

/**
 * Floating DEMO banner — only visible on the preview/development environment.
 *
 * Detection: if the current hostname is `bidtrip.live` (or any production
 * apex/sub of it), we hide the banner. Anywhere else (preview, localhost,
 * *.emergentagent.com, *.emergent.host) it shows.
 *
 * Once the user dismisses it, we remember the choice in localStorage so it
 * doesn't reappear on every refresh — but only for the current host.
 */

const PRODUCTION_HOSTS = new Set(['bidtrip.live', 'www.bidtrip.live']);

const isProductionHost = () => {
  if (typeof window === 'undefined') return true; // be safe on SSR
  return PRODUCTION_HOSTS.has(window.location.hostname);
};

export default function DemoBanner() {
  const [hidden, setHidden] = useState(() => {
    if (typeof window === 'undefined') return true;
    if (isProductionHost()) return true;
    return localStorage.getItem('bidtrip_demo_banner_dismissed') === '1';
  });

  if (hidden) return null;

  const dismiss = () => {
    localStorage.setItem('bidtrip_demo_banner_dismissed', '1');
    setHidden(true);
  };

  return (
    <div
      data-testid="demo-banner"
      className="fixed top-2 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/95 text-foreground shadow-lg backdrop-blur-sm text-[11px] font-bold border border-gold pointer-events-auto"
      style={{ marginTop: 'env(safe-area-inset-top)' }}
    >
      <Sparkles size={12} className="shrink-0" />
      <span>DEMO · Preview</span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss demo banner"
        data-testid="demo-banner-close"
        className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-foreground/15"
      >
        <X size={11} />
      </button>
    </div>
  );
}
